import { useMemo, useState, useEffect } from 'react';
import {
  getSaaSApiBaseUrl,
  getAuthTokenForSaaS,
  parseJsonResponseBody,
  subscribeSaaSAuthUpdated,
} from './teamQueueGoalsApi.js';

function readOrgIdFromContext() {
  const urlParams = new URLSearchParams(
    typeof window !== 'undefined' ? window.location.search : ''
  );
  return (
    urlParams.get('organization_id') ||
    (typeof localStorage !== 'undefined' ? localStorage.getItem('selectedOrganizationId') : null) ||
    (typeof localStorage !== 'undefined' ? localStorage.getItem('organizationId') : null) ||
    (typeof localStorage !== 'undefined' ? localStorage.getItem('organization_id') : null) ||
    null
  );
}

/**
 * Org id + supervisor role + org members for Team queue goals editor (SaaS).
 * Same resolution as Denial TeamAnalyticsDenial.
 */
export default function useTeamQueueGoalsSupervisor() {
  const [availableUsers, setAvailableUsers] = useState([]);
  const [isSupervisor, setIsSupervisor] = useState(false);
  const [organizationId, setOrganizationId] = useState(() => readOrgIdFromContext());
  const [authEpoch, setAuthEpoch] = useState(0);
  const saasBase = useMemo(() => getSaaSApiBaseUrl(), []);

  useEffect(() => {
    setOrganizationId(readOrgIdFromContext());
    const onAuth = () => {
      setOrganizationId(readOrgIdFromContext());
      setAuthEpoch((n) => n + 1);
    };
    return subscribeSaaSAuthUpdated(onAuth);
  }, []);

  useEffect(() => {
    if (!organizationId) return;
    let cancelled = false;
    (async () => {
      try {
        const token = getAuthTokenForSaaS();
        if (!token) return;
        const base = getSaaSApiBaseUrl();
        const headers = { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };
        const byOrg = `${base}/api/v1/organizations/${encodeURIComponent(organizationId)}/members`;
        const legacy = `${base}/api/v1/organizations/current/members?organization_id=${encodeURIComponent(organizationId)}`;
        let response = await fetch(byOrg, { headers });
        if (!response.ok && (response.status === 404 || response.status === 405)) {
          response = await fetch(legacy, { headers });
        }
        if (!response.ok || cancelled) return;
        const data = await parseJsonResponseBody(response);
        if (!data) return;
        const members = Array.isArray(data) ? data : data.members || data.results || [];
        if (!cancelled) setAvailableUsers(members);
      } catch (e) {
        console.warn('[useTeamQueueGoalsSupervisor] members', e);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [organizationId, authEpoch]);

  useEffect(() => {
    if (!organizationId) return;
    let cancelled = false;
    (async () => {
      try {
        const token = getAuthTokenForSaaS();
        if (!token) return;
        const base = getSaaSApiBaseUrl();
        const response = await fetch(`${base}/api/v1/organizations/current`, {
          headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        });
        if (!response.ok || cancelled) return;
        const data = await parseJsonResponseBody(response);
        if (!data) return;
        const role = data.current_user_role || data.role || '';
        if (!cancelled) {
          setIsSupervisor(['owner', 'admin', 'supervisor'].includes(String(role).toLowerCase()));
        }
      } catch (e) {
        console.warn('[useTeamQueueGoalsSupervisor] role', e);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [organizationId, authEpoch]);

  return { organizationId, isSupervisor, availableUsers, saasBase };
}
